import {
  MicrofrontendEvents,
  onAcceptTermsAndConditions,
  subscribe,
} from '@sennder/senn-node-microfrontend-event-bus'

import { getStateProfile } from '@/store/getters'
import router from '@/router'
import { logout } from '@/store/logoutActions'
import { TERMS_ROUTE_NAME } from '@/config/routes'
import { featureFlag } from '@/services/featureFlagProvider'

const MARKETING_OPT_IN_FLAG = 'ENABLE_CUS-MARKETING-OPT-IN'

const handleTermsResponse = async (payload: onAcceptTermsAndConditions) => {
  if (!payload.accepted) {
    await logout()
    return
  }

  let routePath = '/'
  if (router.currentRoute.value.query.redirectTo) {
    routePath = router.currentRoute.value.query.redirectTo.toString()
  }
  router.push(routePath)
}

export const redirectToTerms = async () => {
  await router.isReady()

  const currentRoute = router.currentRoute.value
  const routeName = currentRoute.name?.toString() || ''

  if (routeName !== TERMS_ROUTE_NAME) {
    await router.push({
      name: TERMS_ROUTE_NAME,
      query: {
        redirectTo: currentRoute.fullPath.toString(),
      },
    })
  }
}

/**
 * Checks if the user has accepted the latest terms or needs to provide marketing consent,
 * and redirects to the terms page if necessary.
 *
 * This function performs the following checks:
 * 1. Retrieves the current user state.
 * 2. Determines if the user needs to accept marketing opt-in.
 * 3. Determines if the marketing opt-in modal should be shown.
 * 4. Redirects to the terms page if the user has not accepted the latest terms or if the marketing opt-in modal should be shown.
 */
export const checkUserTermsOrConsentAndRedirect = () => {
  const profile = getStateProfile()
  const consent = profile.consent

  // The terms MF only shows the marketing popup when this flag is on, so
  // redirecting without it just flashes the terms page with nothing to show.
  // The flag value is currently incorrect and will be flipped once SFMC is
  // no longer called, see https://dev.azure.com/sennder/sennder/_workitems/edit/135437
  const isMarketingOptInEnabled =
    featureFlag.getFeatureFlag(MARKETING_OPT_IN_FLAG) === true

  // Redirect the user only if he has not accepted or declined marketing emails
  const shouldShowMarketingOptInModal =
    isMarketingOptInEnabled && consent && consent.marketingOptInAt === null

  if (!profile.hasAcceptedLatestTerms || shouldShowMarketingOptInModal) {
    redirectToTerms()
  }
}

export const registerModalManager = () => {
  subscribe<onAcceptTermsAndConditions>(
    MicrofrontendEvents.onAcceptTermsAndConditions,
    (payload) => handleTermsResponse(payload)
  )
}
